// packages/components/src/agent_profile/showcase/ShowcaseStickyRail.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Button, Badge, Card } from '@dopomogai/ui';
import { BuilderAgentChat } from '../agent/BuilderAgentChat';
import { AvatarStageLite, ShowcaseTier } from './AvatarStageLite';
import { InvestTeaser } from '../invest/InvestTeaser';
import type { InvestmentOfferingConfig, InvestmentStats } from '@dopomogai/types';
import { useBottomSheet, BottomSheetState } from './hooks/useBottomSheet';

type Skill = { key: string; label: string; tooltip?: string };

type CTA = { text: string; onClick: () => void; ariaLabel?: string };

type ChatConfig = {
  toolRegistry: any;
  headlessTools: any;
  headlessContext: any;
  agentAvatarUrl?: string;
};

type MobileMode = {
  enabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialState?: BottomSheetState;
  title?: string;
};

type Props = {
  avatars: {
    trainee?: string;
    junior?: string;
    middle?: string;
    senior?: string;
    poster?: string;
  };
  currentTier: ShowcaseTier;

  skills: Skill[];
  onSkillClick: (key: string) => void;

  primaryCta: CTA;
  secondaryCta?: CTA;
  investCta?: CTA & { visible?: boolean };

  // Make chat optional so host-only usage compiles cleanly
  chat?: ChatConfig;
  chatHost?: React.ReactNode;

  etaText?: string;

  investmentOffering?: InvestmentOfferingConfig | null;
  investmentStats?: InvestmentStats | null;
  agentId?: string;
  onInvestClick?: (agentId: string) => void;

  hideSkills?: boolean;
  hideInvest?: boolean;
  hideCtas?: boolean;

  mobileMode?: MobileMode;
};

export function ShowcaseStickyRail({
  avatars,
  currentTier,
  skills,
  onSkillClick,
  primaryCta,
  secondaryCta,
  investCta,
  chat,
  chatHost,
  etaText,
  investmentOffering,
  investmentStats,
  agentId,
  onInvestClick,
  hideSkills = false,
  hideInvest = false,
  hideCtas = true,
  mobileMode,
}: Props) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };
  const itemVariants = {
    hidden: { y: 18, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 24 } },
  };

  const currentTierAvatar =
    currentTier === 'senior'
      ? avatars.senior
      : currentTier === 'middle'
      ? avatars.middle
      : currentTier === 'junior'
      ? avatars.junior
      : avatars.trainee || avatars.poster;

  // Mobile bottom sheet (lg:hidden)
  const mobileSheetEnabled = Boolean(mobileMode?.enabled);
  const mobileInitialState: BottomSheetState = mobileMode?.initialState || 'short';
  const { styles: sheetStyles } = useBottomSheet(Boolean(mobileMode?.open), mobileInitialState, true);

  return (
    <>
      {/* Desktop/tablet rail */}
      <motion.div
        className={[
          'hidden lg:block',
          'space-y-3 px-1 md:px-1.5',
          'lg:sticky lg:h-fit lg:overflow-auto',
        ].join(' ')}
        style={{
          top: 'var(--site-header-h, 80px)',
          maxHeight: 'calc(100vh - var(--site-header-h, 80px) - 16px)',
        }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Avatar + Chat */}
        <motion.div variants={itemVariants}>
          <Card noPadding className="overflow-hidden rounded-xl flex flex-col">
            <div className="p-2 shrink-0">
              <AvatarStageLite avatars={avatars} currentTier={currentTier} size="sm" variant="flat" />
            </div>
            <div className="border-t-2 border-black" />
            {/* IMPORTANT: give host a real height so docking can happen */}
            <div className="p-2">
              <div className="w-full min-h-[520px]">
                {chatHost
                  ? chatHost
                  : chat
                  ? (
                    <BuilderAgentChat
                      toolRegistry={chat.toolRegistry}
                      headlessTools={chat.headlessTools}
                      headlessContext={chat.headlessContext}
                      agentAvatarUrl={chat.agentAvatarUrl || currentTierAvatar}
                    />
                  )
                  : null}
              </div>
            </div>
          </Card>
        </motion.div>

        {!hideSkills && (
          <motion.div variants={itemVariants}>
            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">Core skills</div>
              <div className="flex flex-wrap gap-2">
                {skills.slice(0, 12).map((s) => (
                  <Badge
                    key={s.key}
                    text={s.label}
                    accentColor="gray"
                    className="cursor-pointer transform transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_rgb(17,24,39)]"
                    onClick={() => onSkillClick(s.key)}
                  />
                ))}
              </div>
              {etaText && (
                <div className="text-xs text-slate-600 bg-slate-50 border-2 border-black rounded-lg px-3 py-2 inline-flex items-center gap-2">
                  <span className="font-semibold text-slate-800">ETA:</span> {etaText}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {!hideInvest && investmentOffering && investmentStats && (
          <motion.div variants={itemVariants}>
            <InvestTeaser
              agentId={agentId || ''}
              offering={investmentOffering}
              stats={investmentStats}
              onInvestClick={onInvestClick}
            />
          </motion.div>
        )}

        {!hideCtas && (
          <motion.div variants={itemVariants}>
            <Card>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  text={primaryCta.text}
                  onClick={primaryCta.onClick}
                  aria-label={primaryCta.ariaLabel || 'Customize and get a quote'}
                  fullWidth
                  className="!py-3.5"
                />
                {secondaryCta && (
                  <Button
                    variant="outline"
                    text={secondaryCta.text}
                    onClick={secondaryCta.onClick}
                    aria-label={secondaryCta.ariaLabel || 'Book a discovery call'}
                    fullWidth
                  />
                )}
                {investCta?.visible && (
                  <Button
                    variant="secondary"
                    text={investCta.text}
                    onClick={investCta.onClick}
                    aria-label={investCta.ariaLabel || 'Invest'}
                    fullWidth
                  />
                )}
              </div>
            </Card>
          </motion.div>
        )}
      </motion.div>

      {/* Mobile bottom sheet for chat */}
      {mobileSheetEnabled && (
        <>
          <div
            className={[
              'lg:hidden fixed inset-0 bg-black/40 transition-opacity duration-200',
              mobileMode?.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
              'z-40',
            ].join(' ')}
            aria-hidden={!mobileMode?.open}
            onClick={() => mobileMode?.onOpenChange(false)}
          />

          <div
            className={[
              'lg:hidden fixed left-0 right-0 bottom-0',
              'border-t-2 border-black rounded-t-2xl',
              'bg-white',
              'z-50',
              'transition-transform duration-200 will-change-transform',
              'shadow-[0_-10px_30px_rgba(0,0,0,0.2)]',
              'flex flex-col',
            ].join(' ')}
            style={{
              ...sheetStyles,
              paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
            }}
            role="dialog"
            aria-modal={mobileMode?.open ? true : false}
            aria-label={mobileMode?.title || 'Chat'}
          >
            <div className="py-2 px-4">
              <div className="flex items-center justify-center">
                <div className="w-10 h-1.5 rounded-full bg-slate-300" />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm font-bold text-slate-800">
                  {mobileMode?.title || 'Chat'}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => mobileMode?.onOpenChange(false)}
                  text="Hide"
                  aria-label="Hide chat"
                />
              </div>
            </div>

            <div className="border-t-2 border-black" />

            {/* IMPORTANT: min height for mobile dock, too */}
            <div className="p-2">
              <div className="w-full min-h-[320px]">
                {chatHost
                  ? chatHost
                  : chat
                  ? (
                    <BuilderAgentChat
                      toolRegistry={chat.toolRegistry}
                      headlessTools={chat.headlessTools}
                      headlessContext={chat.headlessContext}
                      agentAvatarUrl={chat.agentAvatarUrl || currentTierAvatar}
                    />
                  )
                  : null}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default ShowcaseStickyRail;
